import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  detectLocale,
  formatMessage,
  getApiKeysGuideMessages,
  getMessages,
  persistLocale,
} from './index';
import type { ApiKeysGuideMessages } from './guideTypes';
import type { LandingMessages, Locale } from './types';

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: LandingMessages;
  guide: ApiKeysGuideMessages;
  format: (template: string, vars: Record<string, string | number>) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    persistLocale(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = getMessages(locale).documentTitle;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: getMessages(locale),
      guide: getApiKeysGuideMessages(locale),
      format: formatMessage,
    }),
    [locale, setLocale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return ctx;
}
